import { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth";
import { getProfile } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { sanitizeHtml } from "@/lib/sanitize";
import {
  Users,
  Ticket,
  DollarSign,
  ChevronRight,
  Loader2,
  Pencil,
  MapPin,
  Globe,
  Mail,
} from "lucide-react";
import { useNavigate, Link } from "react-router-dom";

export default function Home() {
  const { user } = useAuth();
  const nav = useNavigate();
  const recId = user?.user_record_id || user?.record_id;

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(!!recId);

  useEffect(() => {
    if (!recId) return;
    const ac = new AbortController();
    const run = async () => {
      try {
        const res = await getProfile(recId);
        if (ac.signal.aborted) return;
        setProfile(res?.profile || null);
      } catch {
        /* swallow */
      } finally {
        if (!ac.signal.aborted) setLoading(false);
      }
    };
    run();
    return () => ac.abort();
  }, [recId]);

  const homeHtml = (profile?.home_html || "").trim();
  const firstName = profile?.first_name || user?.first_name || "there";
  const lastName = profile?.last_name || user?.last_name || "";
  const fullName = `${firstName} ${lastName}`.trim();
  const initials = (
    (firstName?.[0] || "").toUpperCase() + (lastName?.[0] || "").toUpperCase()
  );
  const photo = profile?.image_url;
  const companyName = profile?.company_name;
  const cityState = [profile?.city, profile?.state].filter(Boolean).join(", ");
  const website = profile?.website_url || profile?.website;

  const quickLinks = [
    { to: "/app/contacts", icon: Users, label: "My contacts", sub: "View & message your list" },
    { to: "/app/coupons", icon: Ticket, label: "Coupons", sub: "Share your discount codes" },
    { to: "/app/earnings", icon: DollarSign, label: "Orders", sub: "Track team & commissions" },
  ];

  if (loading) {
    return (
      <div
        className="px-4 pt-10 flex flex-col items-center text-zinc-500"
        data-testid="home-loading"
      >
        <Loader2 className="animate-spin mb-2" />
        <span className="text-sm">Loading home…</span>
      </div>
    );
  }

  return (
    <div className="px-4 pt-5 pb-2 space-y-4" data-testid="home-page">
      {/* === 1. HTML widget (always first when set) === */}
      {homeHtml ? (
        <section data-testid="home-html-section">
          <div className="flex items-center justify-between mb-2 px-1">
            <div className="text-[10px] uppercase tracking-widest text-violet-700 font-semibold">
              Home
            </div>
            <Link
              to="/app/profile"
              className="inline-flex items-center gap-1 text-xs font-medium text-zinc-500 hover:text-violet-700"
              data-testid="home-edit-link"
            >
              <Pencil size={11} /> Edit
            </Link>
          </div>
          <Card className="rounded-2xl border-zinc-200 p-5 bg-white overflow-hidden">
            <div
              className="prose prose-sm max-w-none text-zinc-800"
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(homeHtml) }}
              data-testid="home-html-render"
            />
          </Card>
        </section>
      ) : (
        <Card
          className="rounded-2xl border-dashed border-violet-200 bg-violet-50/40 p-5"
          data-testid="home-personalise-card"
        >
          <div className="text-xs font-semibold text-violet-700 uppercase tracking-widest mb-1">
            Personalise this page
          </div>
          <div className="font-semibold text-zinc-900 mb-1">
            Add your own Home HTML widget
          </div>
          <div className="text-sm text-zinc-600 mb-3">
            Paste a custom HTML block from your mindfulminerals.com pages — it
            will render here as your Home tab.
          </div>
          <Link
            to="/app/profile"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-violet-700 hover:underline"
            data-testid="home-add-html-link"
          >
            <Pencil size={13} /> Open profile editor →
          </Link>
        </Card>
      )}

      {/* === 2. vCard — dealer's personal card === */}
      <section data-testid="home-vcard-section">
        <Card
          className="rounded-2xl border-zinc-200 overflow-hidden p-0"
          data-testid="home-vcard"
        >
          <div className="bg-gradient-to-br from-violet-600 via-violet-700 to-fuchsia-700 h-20" />
          <div className="px-5 pb-5 -mt-10">
            <div className="flex items-end gap-3">
              {photo ? (
                <img
                  src={photo}
                  alt={fullName}
                  className="w-20 h-20 rounded-2xl object-cover border-4 border-white shadow"
                  onError={(e) => (e.currentTarget.style.display = "none")}
                  data-testid="home-vcard-photo"
                />
              ) : (
                <div
                  className="w-20 h-20 rounded-2xl bg-violet-600 text-white flex items-center justify-center text-xl font-semibold border-4 border-white shadow"
                  data-testid="home-vcard-initials"
                >
                  {initials || "?"}
                </div>
              )}
            </div>
            <div className="mt-3">
              <div className="text-lg font-semibold text-zinc-900" data-testid="home-vcard-name">
                {fullName}
              </div>
              {companyName && (
                <div className="text-sm text-zinc-700">{companyName}</div>
              )}
              <div className="mt-2 space-y-1 text-xs text-zinc-500">
                {cityState && (
                  <div className="flex items-center gap-1.5">
                    <MapPin size={12} /> {cityState}
                  </div>
                )}
                {profile?.email_id && (
                  <div className="flex items-center gap-1.5 truncate">
                    <Mail size={12} /> {profile.email_id}
                  </div>
                )}
                {website && (
                  <a
                    href={website.startsWith("http") ? website : `https://${website}`}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center gap-1.5 text-violet-700 hover:underline"
                  >
                    <Globe size={12} /> {website.replace(/^https?:\/\//, "")}
                  </a>
                )}
              </div>
            </div>
          </div>
        </Card>
      </section>

      {/* === 3. Quick links to other tabs === */}
      <section data-testid="home-quicklinks-section">
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold mb-2 px-1">
          Quick links
        </div>
        <div className="grid grid-cols-1 gap-2">
          {quickLinks.map(({ to, icon: Icon, label, sub }) => (
            <Card
              key={to}
              onClick={() => nav(to)}
              className="cursor-pointer rounded-2xl border-zinc-200 hover:border-violet-300 hover:bg-violet-50/40 transition-colors p-4 flex items-center gap-4"
              data-testid={`home-link-${label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <div className="w-11 h-11 rounded-xl bg-violet-100 flex items-center justify-center">
                <Icon className="text-violet-700" size={22} />
              </div>
              <div className="flex-1">
                <div className="font-medium text-zinc-900">{label}</div>
                <div className="text-xs text-zinc-500">{sub}</div>
              </div>
              <ChevronRight className="text-zinc-400" size={18} />
            </Card>
          ))}
        </div>
      </section>
      {/* ContactForm is mounted globally in AppShell — appears collapsed below. */}
    </div>
  );
}
